import { useSlackUserConversationsList } from "@/components/EntitySelector/integrations/SlackSelector";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useIntegrationConnection } from "@/hooks/useIntegrationConnection";
import {
  Channel,
  ChannelEntity,
  SlackChannelEntity,
} from "@cohesive/api-entities";
import { getEntityId, Spinner } from "@cohesive/ui-components";
import { ControlProps, optionIs, rankWith } from "@jsonforms/core";
import { withJsonFormsControlProps } from "@jsonforms/react";
import { ChevronsUpDown, Hash, X } from "lucide-react";
import React, { useMemo, useState } from "react";

type ChannelMap = {
  shared: Channel[];
  extShared: Channel[];
  channels: Channel[];
  dms: Channel[];
  apps: Channel[];
};

interface SlackChannelControlProps extends ControlProps {
  data: SlackChannelEntity | string | undefined;
}

const SlackChannelControlComponent: React.FC<SlackChannelControlProps> = ({
  data,
  enabled,
  handleChange,
  path,
  label,
  visible,
  id,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const integration = useIntegrationConnection("slack");

  // Extract ID from data (handles both string ID and entity object)
  // Handle both string IDs and entity objects
  const selectedId =
    typeof data === "string" ? data : data ? getEntityId(data as any) : null;

  const { data: channelsData, isLoading } = useSlackUserConversationsList({
    connection: integration!,
    limit: 1000,
    types: "public_channel,private_channel,im,mpim",
    exclude_archived: true,
  });

  const channels: ChannelEntity[] = channelsData || [];

  const channelMap: ChannelMap = useMemo(() => {
    const shared: Channel[] = [];
    const channels: Channel[] = [];
    const dms: Channel[] = [];
    const extShared: Channel[] = [];
    const apps: Channel[] = [];

    if (!channelsData) return { shared, extShared, channels, dms, apps };

    for (const entity of channelsData) {
      const c = entity.data;
      if (c.is_shared) {
        shared.push(c);
      } else if (c.is_channel && !c.is_mpim) {
        channels.push(c);
      } else if (c.is_ext_shared) {
        extShared.push(c);
      } else if (c.is_bot) {
        apps.push(c);
      } else if (c.is_im || c.is_mpim) {
        dms.push(c);
      }
    }
    return {
      shared: shared.sort((a, b) => a.name.localeCompare(b.name)),
      extShared: extShared.sort((a, b) => a.name.localeCompare(b.name)),
      channels: channels.sort((a, b) => a.name.localeCompare(b.name)),
      dms: dms.sort((a, b) => a.name.localeCompare(b.name)),
      apps: apps.sort((a, b) => a.name.localeCompare(b.name)),
    };
  }, [channelsData]);

  const filterFn = (channel: Channel) => {
    const { name = "" } = channel;

    return name.toLowerCase().includes(searchQuery.toLowerCase());
  };

  const renderChannelIcon = (channel: Channel) => {
    if (channel.im_user_image_url) {
      return (
        <img
          src={channel.im_user_image_url}
          alt={channel.name}
          className="w-7 h-7 shrink-0 rounded-full"
        />
      );
    }

    return (
      <div className="flex items-center justify-center w-7 h-7 shrink-0 rounded-full bg-muted">
        <Hash className="w-4! h-4!" />
      </div>
    );
  };

  const renderFn = (channel: Channel) => {
    return (
      <CommandItem
        key={channel.id}
        value={channel.id}
        onSelect={() => {
          // Return full entity object with base and data properties
          handleChange(path, {
            type: "slack_channel",
            base: "channel",
            platform: "slack",
            data: channel,
          });
          setOpen(false);
        }}
      >
        <div className="flex items-center gap-2">
          {renderChannelIcon(channel)}
          <span>{channel.name}</span>
        </div>
      </CommandItem>
    );
  };

  const selectedChannel = useMemo(
    () => channels.find((channel) => selectedId === channel.data.id),
    [channels, selectedId],
  );

  return (
    <div hidden={!visible} id={id}>
      {label && <label className="text-sm font-medium">{label}</label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            disabled={!enabled}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            {selectedChannel ? (
              <div className="flex items-center gap-2 overflow-hidden">
                {renderChannelIcon(selectedChannel.data)}
                <span className="truncate">{selectedChannel.data.name}</span>
              </div>
            ) : (
              <span className="text-muted-foreground">
                Select Slack channel...
              </span>
            )}
            <div className="flex items-center gap-1 ml-2 shrink-0">
              {selectedChannel && (
                <div
                  role="button"
                  aria-label="Clear selection"
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleChange(path, null);
                  }}
                >
                  <X className="h-4 w-4 opacity-50 hover:opacity-100" />
                </div>
              )}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search channels..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              {isLoading ? (
                <div className="flex items-center justify-center p-4">
                  <Spinner size="sm" />
                </div>
              ) : (
                <>
                  <CommandEmpty>No channels found.</CommandEmpty>
                  <CommandGroup heading="Shared Channels">
                    {channelMap.shared.filter(filterFn).map(renderFn)}
                  </CommandGroup>
                  <CommandGroup heading="Channels">
                    {channelMap.channels.filter(filterFn).map(renderFn)}
                  </CommandGroup>
                  <CommandGroup heading="Direct Messages">
                    {channelMap.dms.filter(filterFn).map(renderFn)}
                  </CommandGroup>
                  <CommandGroup heading="External Shared Channels">
                    {channelMap.extShared.filter(filterFn).map(renderFn)}
                  </CommandGroup>
                  <CommandGroup heading="Apps">
                    {channelMap.apps.filter(filterFn).map(renderFn)}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export const slackChannelTester = rankWith(
  5,
  optionIs("control", "slack_channel"),
);
export const SlackChannelControl = withJsonFormsControlProps(
  SlackChannelControlComponent,
);
